import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { GeneralInquiryDocument } from '@willow/graphql-iso/src/portal';
import { phoneNumberFormat } from '@willow/shared-iso';
import { Button, ControlledInput, NamedMemo, useZodHookForm, z } from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';

import { PortalSelectedLoan } from '../../App';
import { WillowModal } from '../modal/Modal';
import { ManageLoanHeader } from './Header';

interface Props {
  loan: PortalSelectedLoan;
}

const FormShape = z.object({
  message: z.string().min(10, {
    error: 'Enter a minimum of 10 characters',
  }),
});

export const GeneralInquiry = NamedMemo<Props>('GeneralInquiry', ({ loan }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generalInquiry] = useMutation(GeneralInquiryDocument);

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = useZodHookForm(FormShape, {
    defaultValues: {
      message: '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await generalInquiry({
        variables: {
          companyId: loan.company.id,
          loanId: loan.id,
          message: data.message,
        },
      });

      setShowSuccessModal(true);
      reset();
    } catch {
      toast.error(`Something went wrong submitting the inquiry form. Please try again.`);
    }
  });

  return (
    <>
      <ManageLoanHeader
        title="Send a General Inquiry"
        subtitle={
          <>
            If you require immediate support please call our servicing team at{' '}
            <a href={`tel: ${loan.company.supportPhone}`} aria-label={`phone number: ${loan.company.supportPhone}`}>
              {phoneNumberFormat(loan.company.supportPhone)}
            </a>{' '}
            or via email at <a href={`mailto:${loan.company.supportEmail}`}>{loan.company.supportEmail}</a>.
          </>
        }
      />

      <Form onSubmit={onSubmit}>
        <Form.Group className="w-100">
          <ControlledInput
            control={control}
            fieldName="message"
            placeholder="Enter your note"
            inputType="textarea"
            textareaRows={8}
          />
        </Form.Group>

        <div className="mt-4 w-100 d-flex justify-content-end">
          <Button variant="primary" size="md" onClick={onSubmit} loading={isSubmitting}>
            Submit Inquiry
          </Button>
        </div>
      </Form>

      <WillowModal showModal={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <h2 className="u-fs-4 u-bold mb-2">Your inquiry has been sent</h2>
        <div>
          Our team is reviewing your note. We will be in touch shortly to discuss options. For immediate assistance
          please call us at{' '}
          <a href={`tel: ${loan.company.supportPhone}`} aria-label={`phone number: ${loan.company.supportPhone}`}>
            {phoneNumberFormat(loan.company.supportPhone)}
          </a>
          .
        </div>
        <div className="mt-4 ms-auto">
          <Button
            variant="primary"
            size="sm"
            className="hover-override hover:u-bg-secondary"
            onClick={() => setShowSuccessModal(false)}
          >
            Close
          </Button>
        </div>
      </WillowModal>
    </>
  );
});
